// checkout/utils/formStatus.ts

import { checkoutData } from '../init';
import { updateCheckoutStepUrl } from './updateCheckoutStepUrl';
import { lockFocusable, restoreFocusable } from './preserveTabindex';

/**
 * Updates a form's visual state and accessibility attributes
 * based on its current step status, and manages tabbable elements.
 *
 * @param {string} formId - The ID of the form element.
 * @param {string} stepId - The key of the step in checkoutData.
 * @returns {void}
 */
export function updateFormStatusClass(formId: string, stepId: string): void {
	const form = document.getElementById(formId) as HTMLFormElement | null;
	if (!form) return;

	const formParent =
		formId === 'registrationForm' || formId === 'loginForm'
			? form.closest('.checkout__auth')
			: form.closest('.checkout__content');
	if (!formParent) return;

	const status = checkoutData[stepId]?.status || 'LOCKED';
	const statusClass = '--' + status.toLowerCase();

	formParent.classList.remove('step--focused', 'step--complete', 'step--locked');

	// Exclude toggle triggers; don't alter their tabindex
	const controls = Array.from(
		form.querySelectorAll<HTMLElement>(
			'input, select, textarea, button, a[href], [tabindex]:not([data-form-target]):not([data-target])',
		),
	);

	switch (status) {
		case 'FOCUSED': {
			form.removeAttribute('aria-hidden');
			form.removeAttribute('hidden');
			form.setAttribute('tabindex', '-1');

			// Restore only for controls NOT inside hidden rows
			controls.forEach((el) => {
				if (el.closest('.form__row--hidden')) return;
				restoreFocusable(el);
			});

			updateCheckoutStepUrl(formId);
			break;
		}

		case 'COMPLETE': {
			if (form.contains(document.activeElement)) {
				(document.activeElement as HTMLElement).blur();
			}
			form.setAttribute('aria-hidden', 'true');
			form.setAttribute('hidden', '');
			form.removeAttribute('tabindex');
			controls.forEach(lockFocusable);
			break;
		}

		default: {
			if (form.contains(document.activeElement)) {
				(document.activeElement as HTMLElement).blur();
			}
			form.setAttribute('aria-hidden', 'true');
			form.setAttribute('hidden', '');
			form.removeAttribute('tabindex');
			controls.forEach(lockFocusable);
			break;
		}
	}

	formParent.classList.add('step' + statusClass);
}
